import { useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, Percent, Calendar, HelpCircle, Info, Car } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { NumericInput } from '@/components/ui/numeric-input';
import { LoanTypeSelector } from '@/components/LoanTypeSelector';
import { calculateLoanSchedule, LoanPreset, loanPresets, formatCurrency } from '@/lib/loanCalculator';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getUserCurrencySymbol } from '@/utils/locale';

export interface EMICalculatorData {
  principal: number;
  rate: number;
  tenureYears: number;
  tenureMonths: number;
  interestType: 'reducing' | 'flat';
  loanTypeId: string;
  vehiclePrice: number;
  downPayment: number;
  result: ReturnType<typeof calculateLoanSchedule> | null;
}


interface EMICalculatorProps {
  data: EMICalculatorData;
  onDataChange: (updates: Partial<EMICalculatorData>) => void;
  onCalculate: (result: ReturnType<typeof calculateLoanSchedule>) => void;
}

export function EMICalculator({ data, onDataChange, onCalculate }: EMICalculatorProps) {
  const {
    principal,
    rate,
    tenureYears,
    tenureMonths,
    interestType,
    loanTypeId,
    vehiclePrice,
    downPayment,
  } = data;

  const loanType = loanPresets.find(p => p.id === loanTypeId) || loanPresets[0];
  const isVehicleLoan = loanTypeId === 'vehicle';

  // Setters that update via parent's updateTabData
  const setPrincipal = useCallback((v: number) => {
    onDataChange({ principal: Math.max(0, v) });
  }, [onDataChange]);

  const setRate = useCallback((v: number) => {
    onDataChange({ rate: Math.max(0, Math.min(30, v)) });
  }, [onDataChange]);

  const setTenureYears = useCallback((v: number) => {
    onDataChange({ tenureYears: Math.max(0, Math.min(30, v)) });
  }, [onDataChange]);

  const setTenureMonths = useCallback((v: number) => {
    onDataChange({ tenureMonths: Math.max(0, Math.min(11, v)) });
  }, [onDataChange]);

  const setInterestType = useCallback((v: 'reducing' | 'flat') => {
    onDataChange({ interestType: v });
  }, [onDataChange]);

  const setVehiclePrice = useCallback((v: number) => {
    onDataChange({ vehiclePrice: Math.max(0, v) });
  }, [onDataChange]);

  const setDownPayment = useCallback((v: number) => {
    onDataChange({ downPayment: Math.max(0, v) });
  }, [onDataChange]);

  // Bidirectional sync for vehicle loan fields
  const handleVehiclePriceChange = (value: number) => {
    const newPrice = Math.max(0, value);
    const newLoan = Math.max(0, newPrice - downPayment);
    onDataChange({ vehiclePrice: newPrice, principal: newLoan });
  };

  const handleDownPaymentChange = (value: number) => {
    const clampedValue = Math.max(0, Math.min(vehiclePrice, value));
    const newLoan = Math.max(0, vehiclePrice - clampedValue);
    onDataChange({ downPayment: clampedValue, principal: newLoan });
  };

  const handleVehicleLoanAmountChange = (value: number) => {
    const newPrincipal = Math.max(0, value);
    const newDownPayment = Math.max(0, vehiclePrice - newPrincipal);
    onDataChange({ principal: newPrincipal, downPayment: newDownPayment });
  };

  // Update values when loan type changes
  const handleLoanTypeChange = (preset: LoanPreset) => {
    const defaultPrincipals: Record<string, number> = {
      home: 2500000,
      personal: 500000,
      vehicle: 800000,
      education: 1000000,
      custom: 1000000
    };
    
    if (preset.id === 'vehicle') {
      onDataChange({
        loanTypeId: preset.id,
        rate: preset.defaultRate,
        tenureYears: preset.defaultTenureYears,
        tenureMonths: 0,
        vehiclePrice: 1000000,
        downPayment: 200000,
        principal: 800000,
      });
    } else {
      onDataChange({
        loanTypeId: preset.id,
        rate: preset.defaultRate,
        tenureYears: preset.defaultTenureYears,
        tenureMonths: 0,
        principal: defaultPrincipals[preset.id] || 1000000,
      });
    }
  };

  // Calculate on any input change
  const result = useMemo(() => {
    const totalMonths = tenureYears * 12 + tenureMonths;
    if (principal <= 0 || rate < 0 || totalMonths <= 0) return null;
    return calculateLoanSchedule({
      principal,
      annualRate: rate,
      tenureMonths: totalMonths,
      interestType
    });
  }, [principal, rate, tenureYears, tenureMonths, interestType]);

  useEffect(() => {
    if (result) {
      onCalculate(result);
    }
  }, [result, onCalculate]);

  const totalMonths = tenureYears * 12 + tenureMonths;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-card rounded-2xl shadow-card border border-border p-4 sm:p-6 lg:p-8"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">EMI Details</h2>
            <Dialog>
              <DialogTrigger asChild>
                <button 
                  aria-label="Information about EMI calculation formula" 
                  className="p-1 rounded-full hover:bg-muted transition-colors"
                >
                  <Info className="w-4 h-4 text-muted-foreground hover:text-primary" />
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Understanding EMI</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <p>
                    <strong className="text-foreground">EMI (Equated Monthly Installment)</strong> is the fixed payment amount made by a borrower to a lender at a specified date each calendar month.
                  </p>
                  <div>
                    <p className="font-medium text-foreground mb-1">EMI Components:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Principal:</strong> The original loan amount borrowed</li>
                      <li><strong>Interest:</strong> The cost of borrowing, calculated on the outstanding principal</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">How it's calculated:</p>
                    <p>EMI = [P × r × (1+r)^n] / [(1+r)^n – 1]</p>
                    <p className="text-xs mt-1">Where P = Principal, r = Monthly interest rate, n = Number of months</p>
                  </div>
                  <p>
                    In the early months, a larger portion goes toward interest. As time progresses, more goes toward reducing the principal.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Loan Type Selection */}
      <div className="mb-8">
        <Label className="text-sm font-medium text-foreground mb-3 block">Loan Type</Label>
        <LoanTypeSelector selected={loanType.id} onSelect={handleLoanTypeChange} />
        <p className="text-xs text-muted-foreground mt-2">{loanType.description}</p>
      </div>

      {/* Interest Type Toggle */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Label className="text-sm font-medium text-foreground">Interest Type</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                type="button" 
                aria-label="Interest calculation method explanation"
                className="p-0.5 rounded-full hover:bg-muted"
              >
                <HelpCircle className="w-4 h-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">
                <strong>Reducing Balance:</strong> Interest calculated on remaining principal. Most common for home loans.
                <br /><br />
                <strong>Flat Rate:</strong> Interest calculated on original principal throughout. Common for personal loans.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="inline-flex p-1 bg-muted rounded-xl">
          <button
            onClick={() => setInterestType('reducing')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
              interestType === 'reducing'
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Reducing Balance
          </button>
          <button
            onClick={() => setInterestType('flat')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
              interestType === 'flat'
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Flat Rate
          </button>
        </div>
      </div>

      {/* Vehicle Loan Specific Fields */}
      {isVehicleLoan && (
        <div className="mb-8 p-4 rounded-xl bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 mb-4">
            <Car className="w-4 h-4 text-primary" />
            <Label className="text-sm font-medium text-foreground">Vehicle Details</Label>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Vehicle Price</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{getUserCurrencySymbol()}</span>
                <NumericInput
                  value={vehiclePrice}
                  onChange={(value) => handleVehiclePriceChange(value)}
                  className="text-right font-semibold"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Down Payment</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <NumericInput
                  value={downPayment}
                  onChange={(value) => handleDownPaymentChange(value)}
                  className="text-right font-semibold"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Loan Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <NumericInput
                  value={principal}
                  onChange={(value) => handleVehicleLoanAmountChange(value)}
                  className="text-right font-semibold"
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Loan Amount = Vehicle Price − Down Payment
          </p>
        </div>
      )}

      {/* Loan Amount - Hidden for vehicle loans as it's in the vehicle section */}
      {!isVehicleLoan && (
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-primary" />
              <Label className="text-sm font-medium text-foreground">Loan Amount</Label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">₹</span>
              <NumericInput
                value={principal}
                onChange={(value) => setPrincipal(value)}
                className="w-full sm:w-32 text-right font-semibold"
                min={0}
                max={Number.MAX_SAFE_INTEGER}
              />
            </div>
          </div>
          <Slider
            value={[principal]}
            onValueChange={([value]) => setPrincipal(value)}
            min={loanType.minAmount}
            max={loanType.maxAmount}
            step={loanType.minAmount >= 100000 ? 50000 : 10000}
            className="mt-2"
          />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>{formatCurrency(loanType.minAmount)}</span>
            <span>{formatCurrency(loanType.maxAmount)}</span>
          </div>
        </div>
      )}

      {/* Interest Rate */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Percent className="w-4 h-4 text-primary" />
            <Label className="text-sm font-medium text-foreground">Interest Rate (% p.a.)</Label>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              className="w-20 text-right font-semibold"
              min={0}
              max={30}
              step={0.1}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
        <Slider
          value={[rate]}
          onValueChange={([value]) => setRate(value)}
          min={4}
          max={24}
          step={0.1}
          className="mt-2"
        />
        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          <span>4%</span>
          <span>24%</span>
        </div>
      </div>

      {/* Tenure */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <Label className="text-sm font-medium text-foreground">Loan Tenure</Label>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={tenureYears}
                onChange={(e) => setTenureYears(Number(e.target.value))}
                className="w-14 sm:w-16 text-right font-semibold"
                min={0}
                max={30}
              />
              <span className="text-xs sm:text-sm text-muted-foreground">Yrs</span>
            </div>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={tenureMonths}
                onChange={(e) => setTenureMonths(Number(e.target.value))}
                className="w-14 sm:w-16 text-right font-semibold"
                min={0}
                max={11}
              />
              <span className="text-xs sm:text-sm text-muted-foreground">Mo</span>
            </div>
          </div>
        </div>
        <Slider
          value={[totalMonths]}
          onValueChange={([value]) => {
            setTenureYears(Math.floor(value / 12));
            setTenureMonths(value % 12);
          }}
          min={12}
          max={360}
          step={1}
          className="mt-2"
        />
        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          <span>1 Year</span>
          <span>30 Years</span>
        </div>
      </div>

      {/* Quick tenure options */}
      <div className="flex flex-wrap gap-2">
        {[5, 10, 15, 20, 25, 30].map((years) => (
          <button
            key={years}
            onClick={() => {
              setTenureYears(years);
              setTenureMonths(0);
            }}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200",
              tenureYears === years && tenureMonths === 0
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
            )}
          >
            {years} Yrs
          </button>
        ))}
      </div>
    </motion.div>
  );
}
